<template>
  <container
    :tag="{ value: 'div', props: { class: ['dnd', containerClass] } }"
    :drag-handle-selector="dragHandleSelector || '.dnd__draggable-handle'"
    v-bind="$attrs"
    @drop="handleDrop"
  >
    <draggable
      v-for="(item, index) in dndList"
      :key="item.id || index"
      :tag="{
        value: 'div',
        props: {
          class: ['dnd__draggable box', { 'dnd__draggable--disabled': loading }]
        }
      }"
    >
      <span v-if="!dragHandleSelector" class="dnd__draggable-handle">
        <b-icon icon="drag" />
      </span>
      <span v-if="enableNumbering" class="dnd__draggable-numbering">
        {{ index + 1 }}.
      </span>
      <div class="dnd__draggable-item">
        <slot name="item" :item="item" :index="index" />
      </div>
    </draggable>
  </container>
</template>

<script>
import { Container, Draggable } from 'vue-smooth-dnd';

import { moveByIndex } from '../../utils/array';

/**
 * Generalized `DndList` component.
 *
 * Usage: Example only uses required props
 *  <mc-dnd-list
 *    // List of items to be sorted
 *    :list="Array"
 *
 *    // Generally async will be called with a data object with fields
 *    // `{fromItem, toItem}` where `fromItem` is item which is to be moved to
 *    // `toItem`, If this function rejects then the `DnD` action which caused
 *    // it will be reverted.
 *    :onDrop="Function"
 *    // A function that will sort array according to its sort order, if
 *    // unspecified then will consider the natural order of the list as its
 *    // sort order
 *    :sortHandler="Function"
 *  >
 *    // Named template with scope used to render collection items
 *    <template #item="{item, index}">
 *      <some-item-renderer-component :item="item" :index="index" />
 *    </template>
 *  </mc-dnd-list>
 */
export default {
  components: {
    Container,
    Draggable
  },
  props: {
    containerClass: {
      type: String,
      default: ''
    },
    list: {
      type: Array
    },
    dragHandleSelector: {
      type: String,
      required: false
    },
    onDrop: {
      type: Function,
      /* eslint-disable-next-line */
      default: ({ fromItem, toItem }) => {}
    },
    sortHandler: {
      type: Function,
      required: false
    },
    enableNumbering: {
      type: Boolean,
      default: false
    }
  },
  data: function() {
    return {
      dndList: [],
      loading: false
    };
  },
  watch: {
    list: {
      handler() {
        this.updateDndList();
      },
      immediate: true
    }
  },
  methods: {
    updateDndList() {
      if (this.sortHandler) {
        this.dndList = this.sortHandler(this.list);
      } else {
        this.dndList = [...this.list];
      }
    },
    async handleDrop(dropResult) {
      this.loading = true;
      const { removedIndex: oldPosition, addedIndex: newPosition } = dropResult;
      const fromItem = this.dndList[oldPosition];
      const toItem = this.dndList[newPosition];
      // Moving them locally and revert this if it fails at server
      moveByIndex(this.dndList, oldPosition, newPosition);
      try {
        await this.onDrop({ fromItem, toItem });
      } catch (error) {
        // Will revert any change made due to failure
        this.updateDndList();
        this.$buefy.toast.open({
          message: 'Failed to move!',
          type: 'is-danger'
        });
      }
      this.loading = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.dnd {
  &__draggable {
    display: flex !important;
    align-items: center;

    &:not(:last-child) {
      margin-bottom: 0.5rem;
    }

    &--disabled {
      pointer-events: none;
      filter: grayscale(1);
      opacity: 0.7;
    }
  }

  &__draggable-handle {
    cursor: move;
    margin-right: 1rem;
    flex: 0 0 0;
  }

  &__draggable-numbering {
    flex: 0 0 0;
    margin-right: 0.3rem;
  }

  &__draggable-item {
    flex: 1 1 0;
    // Needed to make flex children to stick to its flex value in cases when
    // content tries to stretch it beyound its limit
    min-width: 0;
  }
}
</style>
